// src/components/DomainDetails.js
import React, { useState, useEffect } from 'react';
import { Card, Tabs, Spin, message, Table, Row, Col } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { getDomain } from '../../services/api';
import DNSRecords from '../DNSManagement/DNSRecords';
import DashboardLayout from './DashboardLayout';

const { TabPane } = Tabs;

const DomainDetails = React.memo(() => {
    const [domain, setDomain] = useState(null);
    const [loading, setLoading] = useState(true);
    const { domainId } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        fetchDomainDetails();
    }, [domainId]);

    const fetchDomainDetails = async () => {
        try {
            const data = await getDomain(domainId);
            setDomain(data);
        } catch (error) {
            console.error('Error fetching domain details:', error);
            message.error('Failed to fetch domain details');
            navigate('/domains'); // Redirect back to domains list on error
        } finally {
            setLoading(false);
        }
    };

    return (
        <DashboardLayout>
            <div style={{ position: 'relative', minHeight: '200px' }}>
                <Spin 
                    spinning={loading} 
                    size="large"
                    style={{
                        position: loading ? 'absolute' : 'static',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)'
                    }}
                >
                    {domain && (
                        <Card>
                            <Tabs defaultActiveKey="overview">
                                <TabPane tab="Overview" key="overview">
                                    <DomainOverview domain={domain} />
                                </TabPane>
                                <TabPane tab="DNS Records" key="dns">
                                    <DNSRecords domain={domain} />
                                </TabPane>
                            </Tabs>
                        </Card>
                    )}
                </Spin>
            </div>
        </DashboardLayout>
    );
});

// Domain Overview Component
const DomainOverview = React.memo(({ domain }) => {
    return (
        <div className="p-4">
            <h2 className="text-xl font-bold mb-4">Domain Information</h2>
            <Card 
                title="Domain Details"
                bordered={true}
                style={{ marginBottom: 16 }}
            >
                {[
                    { label: "Domain Name", value: domain.name },
                    { label: "Status", value: domain.status },
                    { label: "Registration Date", value: new Date(domain.created_at).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                    }) },
                    domain.expiry_date && { 
                        label: "Expiry Date", 
                        value: new Date(domain.expiry_date).toLocaleDateString('en-GB', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                        }) 
                    },
                    domain.price && { label: "Registration Price", value: `₹${domain.price.toFixed(2)}` }
                ].filter(Boolean).map((item, index, arr) => (
                    <Row 
                        key={item.label}
                        gutter={[16, 8]}
                        style={{
                            borderBottom: index !== arr.length - 1 ? '1px solid #f0f0f0' : 'none',
                            paddingBottom: 8,
                            marginBottom: 8
                        }}
                    >
                        <Col span={8} className="font-semibold">{item.label}:</Col>
                        <Col span={16}>{item.value}</Col>
                    </Row>
                ))}
            </Card>
        </div>
    );
});

export default DomainDetails;